import React, { createContext, useContext, useEffect, Dispatch, useReducer } from 'react'
import { getCookieValue } from '@/lib/cookie'
import { useAuth } from '@/context/auth'
import { setCookie } from 'cookies-next'

const ANONYMOUS_MESSAGE_COUNT_COOKIE = 'anonymous_message_count'
export const MAX_ANONYMOUS_MESSAGES = 3

type State = {
  unauthenticatedMessageCount: number
}

type Action = { type: 'INCREMENT_ANONYMOUS_COUNT' } | { type: 'INITIALIZE_ANONYMOUS_COUNT'; payload: number }

interface AnonymousChatContextProps {
  state: State
  dispatch: Dispatch<Action>
  anonymousMessageLimitReached: boolean
  incrementMessageCount: () => void
}

const anonymousChatReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'INCREMENT_ANONYMOUS_COUNT': {
      const newCount = state.unauthenticatedMessageCount + 1
      setCookie(ANONYMOUS_MESSAGE_COUNT_COOKIE, newCount.toString(), {
        maxAge: 24 * 60 * 60,
        path: '/',
      })
      return { ...state, unauthenticatedMessageCount: newCount }
    }
    case 'INITIALIZE_ANONYMOUS_COUNT':
      return { ...state, unauthenticatedMessageCount: action.payload }
    default:
      return state
  }
}

const AnonymousChatContext = createContext<AnonymousChatContextProps | undefined>(undefined)

export const AnonymousChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    state: { user },
  } = useAuth()
  const [state, dispatch] = useReducer(anonymousChatReducer, {
    unauthenticatedMessageCount: 0,
  })
  const cookieCount = getCookieValue(ANONYMOUS_MESSAGE_COUNT_COOKIE)

  useEffect(() => {
    if (user?.id && cookieCount) {
      // Remove the cookie if the user is authenticated
      setCookie(ANONYMOUS_MESSAGE_COUNT_COOKIE, '', {
        maxAge: -1,
        path: '/',
      })
      return
    }
  }, [cookieCount, user?.id])

  useEffect(() => {
    if (cookieCount) {
      const count = parseInt(cookieCount, 10)
      if (!isNaN(count)) {
        dispatch({ type: 'INITIALIZE_ANONYMOUS_COUNT', payload: count })
      }
    }
  }, [cookieCount])

  const anonymousMessageLimitReached = !user?.id && state.unauthenticatedMessageCount >= MAX_ANONYMOUS_MESSAGES

  const incrementMessageCount = (): void => {
    if (!user?.id && state.unauthenticatedMessageCount < MAX_ANONYMOUS_MESSAGES) {
      dispatch({ type: 'INCREMENT_ANONYMOUS_COUNT' })
    }
  }
  return (
    <AnonymousChatContext.Provider
      value={{
        state,
        dispatch,
        anonymousMessageLimitReached,
        incrementMessageCount,
      }}
    >
      {children}
    </AnonymousChatContext.Provider>
  )
}

export const useAnonymousChat = (): AnonymousChatContextProps => {
  const context = useContext(AnonymousChatContext)
  if (!context) {
    throw new Error('useAnonymousChat must be used within an AnonymousChatProvider')
  }
  return context
}
